import { useEffect, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useEasykitPlanningStore } from '@/stores/easykitPlanning';
import { getWeekDates } from '@/utils/planning';
import { EasykitPlanningHeader } from '@/components/easykit/EasykitPlanningHeader';
import { EasykitPlanningGrid } from '@/components/easykit/EasykitPlanningGrid';
import { EasykitTaskDetailPanel } from '@/components/easykit/EasykitTaskDetailPanel';
import { EasykitOverlayDetailPanel } from '@/components/easykit/EasykitOverlayDetailPanel';
import { EasykitMoveBanner } from '@/components/easykit/EasykitMoveBanner';

export function EasykitPlanningView() {
  // Group reads via useShallow so re-renders only fire when these specific
  // fields change — without it, the destructure subscribes to every key change.
  const { loading, error, weekOffset, selectedTaskId, tasks, fetchPlanning, moveMode, createMode, scheduleMode } =
    useEasykitPlanningStore(useShallow((s) => ({
      loading: s.loading,
      error: s.error,
      weekOffset: s.weekOffset,
      selectedTaskId: s.selectedTaskId,
      tasks: s.tasks,
      fetchPlanning: s.fetchPlanning,
      moveMode: s.moveMode,
      createMode: s.createMode,
      scheduleMode: s.scheduleMode,
    })));

  useEffect(() => {
    fetchPlanning();
  }, [fetchPlanning]);

  // Silent background refresh every 30 seconds
  const silentRefresh = useEasykitPlanningStore((s) => s.silentRefresh);
  useEffect(() => {
    const interval = setInterval(silentRefresh, 30_000);
    return () => clearInterval(interval);
  }, [silentRefresh]);

  const weekData = useMemo(() => getWeekDates(weekOffset), [weekOffset]);
  // EasyKit only uses Mon-Fri (5 days), drop Saturday
  const days = useMemo(() => weekData.days.slice(0, 5), [weekData.days]);

  // Find selected task
  const selectedTask = selectedTaskId !== null
    ? tasks.find((t) => t.id === selectedTaskId) ?? null
    : null;

  // Find selected overlay (PL task)
  const selectedOverlayId = useEasykitPlanningStore((s) => s.selectedOverlayId);
  const overlayTasks = useEasykitPlanningStore((s) => s.overlayTasks);
  const selectedOverlay = selectedOverlayId !== null
    ? overlayTasks.find((t) => t.id === selectedOverlayId) ?? null
    : null;

  // Auto-dismissing "Opgeslagen" toast after a successful save (drag or reorder).
  const savedToast = useEasykitPlanningStore((s) => s.savedToast);
  const clearSavedToast = useEasykitPlanningStore((s) => s.clearSavedToast);
  const saveInFlight = useEasykitPlanningStore((s) => s.saveInFlight);
  useEffect(() => {
    if (!savedToast) return;
    const id = setTimeout(clearSavedToast, 1800);
    return () => clearTimeout(id);
  }, [savedToast, clearSavedToast]);

  // Global Escape: exit ANY transient mode (move/create/schedule) AND any open
  // detail panel together — previously Escape only closed the panel, leaving the
  // mode banner stuck.
  const exitMoveMode = useEasykitPlanningStore((s) => s.exitMoveMode);
  const exitCreateMode = useEasykitPlanningStore((s) => s.exitCreateMode);
  const exitScheduleMode = useEasykitPlanningStore((s) => s.exitScheduleMode);
  const selectTask = useEasykitPlanningStore((s) => s.selectTask);
  const selectOverlay = useEasykitPlanningStore((s) => s.selectOverlay);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      exitMoveMode();
      exitCreateMode();
      exitScheduleMode();
      selectTask(null);
      selectOverlay(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [exitMoveMode, exitCreateMode, exitScheduleMode, selectTask, selectOverlay]);

  return (
    <div>
      <EasykitPlanningHeader />

      {/* Move/Create/Schedule mode banner */}
      {(moveMode || createMode || scheduleMode) && <EasykitMoveBanner />}

      {/* Loading state */}
      {loading && (
        <div className="text-center py-12">
          <div
            className="inline-block w-8 h-8 border-4 border-td-blue/30 border-t-td-blue rounded-full animate-spin"
            role="status"
          >
            <span className="sr-only">Laden...</span>
          </div>
          <p className="text-td-muted text-sm mt-3">EasyKit planning laden...</p>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="mx-4 lg:mx-8 bg-red-50 text-red-600 p-4 rounded-xl" role="alert">
          <p className="font-semibold">Fout bij laden</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      )}

      {/* Grid — wrapper carries aria-busy so AT users navigating the grid are
          informed when a save is in flight (overlay is purely visual). */}
      {!loading && !error && (
        <div aria-busy={saveInFlight}>
          <EasykitPlanningGrid days={days} />
        </div>
      )}

      {/* Detail panel — inline below grid (hidden during create mode) */}
      {selectedTask && !createMode && <EasykitTaskDetailPanel task={selectedTask} />}
      {selectedOverlay && !createMode && <EasykitOverlayDetailPanel task={selectedOverlay} />}

      {/* Saving overlay — translucent dim that blocks pointer events, makes the
          short save window unmistakably "busy" instead of silently disabling drag.
          aria-busy lives on the grid wrapper above; aria-live here narrates the state. */}
      {saveInFlight && (
        <div
          className="fixed inset-0 z-40 bg-white/40 cursor-wait select-none"
          role="status"
          aria-live="polite"
        >
          <span className="sr-only">Bezig met opslaan</span>
        </div>
      )}

      {/* Save indicator — bottom-right. Spinner while saving, green check when done. */}
      {saveInFlight && (
        <div
          className="fixed bottom-6 right-6 z-50 bg-slate-700 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 animate-in fade-in"
          role="status"
        >
          <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" aria-hidden="true" />
          <span className="text-sm font-semibold">Bezig met opslaan…</span>
        </div>
      )}
      {!saveInFlight && savedToast && (
        <div
          className="fixed bottom-6 right-6 z-50 bg-emerald-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 animate-in fade-in slide-in-from-bottom-2"
          role="status"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
          </svg>
          <span className="text-sm font-semibold">{savedToast}</span>
        </div>
      )}
    </div>
  );
}
